import { debounceTime, map } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import type { SplitterResizeEndEvent } from "primeng/splitter";
import type { MenuItem } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from "@medsurf/flat-actions";
import * as FlatFacades from '@medsurf/flat-facades';
import { PageTitleDialogComponent } from "../common/page-title-dialog/page-title-dialog.component";

@Component({
    selector: 'medsurf-chapter',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './chapter.component.html',
    styleUrls: ['./chapter.component.scss'],
    standalone: false
})
export class ChapterComponent {
  @ViewChild('node_tree_scroll_panel', {static: false}) nodeTreeScrollPanel: ElementRef<HTMLDivElement>;

  contentsMenuItems$ = this.userSharedFacade.currentUser$.pipe(map((currentUser) => {
    return [
			{
				label: this.translate.instant('global.elearning_add'),
				disabled: currentUser?.role !== FlatModels.UserEntityModels.Role.enum.admin,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ADD,
				command: () => this.addElearning()
			}
		] satisfies MenuItem[];
  }));
  readonly FlatModels = FlatModels;

  constructor(public appSharedFacade: FlatFacades.AppSharedFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              private nodeSharedFacade: FlatFacades.NodeSharedFacade,
              private alertControlFacade: FlatFacades.AlertControlFacade,
              private userSharedFacade: FlatFacades.UserSharedFacade,
              private dialogService: DialogService,
              private translate: TranslateService) {
	  // Close All Open Dialogs
	  this.dialogService.dialogComponentRefMap.forEach(dialog => {
		  dialog.destroy();
	  });
    // Force Show Chapter when hidden
    if (this.slideAuthorFacade.snapshot_chapterPanelData().isMinimized) {
      this.slideAuthorFacade.requestCollapseChapter(false, true).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
    // Scroll Into View The Given Node In The Node Tree
    this.slideAuthorFacade.ofActionDispatchedScrollNodeIntoView.pipe(takeUntilDestroyed(), debounceTime(200)).subscribe((data: { action: FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView }) => {
      const element = document.getElementById('nt-' + data.action.id);
	    if (!element || !this.nodeTreeScrollPanel) return;
      this.nodeTreeScrollPanel.nativeElement.scrollTop = element.offsetTop - element.offsetHeight * 3;
    });
    // Scroll To after navigate from slide component
    this.nodeSharedFacade.isSlide$.pipe(takeUntilDestroyed(), distinctUntilChanged(), debounceTime(200)).subscribe((isSlide) => {
      if (!isSlide) {
        const currentNode = this.slideAuthorFacade.snapshot_currentSelectedNode();
        if (!currentNode) return;
        const element = document.getElementById('nt-' + currentNode.id);
        if (!element || !this.nodeTreeScrollPanel) return;
        this.nodeTreeScrollPanel.nativeElement.scrollTop = element.offsetTop - element.offsetHeight * 3;
      }
    });
  }

  private addElearning() {
    const dialogRef = this.dialogService.open(PageTitleDialogComponent, {
      header: this.translate.instant('global.elearning_add_description'),
      data: {
        inputLabel: this.translate.instant('global.add')
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(data => {
      if (data && data.result) {
        this.nodeSharedFacade.requestAddElearning('history.elearning_add', data.title).subscribe({
          error: (result) => {
            this.alertControlFacade.requestAddError(result);
          },
          next: () => {
            this.slideAuthorFacade.requestCollapseChapter(false, true).subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
            });
          }
        });
      }
    });
  }

  chapterOnResizeStart() {
    this.slideAuthorFacade.requestSetIsSplitterMoving(true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.slideAuthorFacade.requestCollapseChapter(false, true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  chapterOnResizeEnd(event: SplitterResizeEndEvent) {
    this.slideAuthorFacade.requestSetIsSplitterMoving(false).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.slideAuthorFacade.requestSetChapterPanelSize(event.sizes).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  chapterCollapsedChange(chapterIsMinimized: boolean) {
    this.slideAuthorFacade.requestCollapseChapter(chapterIsMinimized).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
}
